/**
 * PlanGate — wraps a page (or section) and blocks it behind an upgrade
 * prompt when the user's effective plan doesn't include the given feature.
 *
 * Usage:
 *   <PlanGate feature="chat" title="Chat is a paid feature">
 *     <ChatPage />
 *   </PlanGate>
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock, CheckCircle2, Zap, ArrowRight } from 'lucide-react';
import { hasFeature, minPlanFor, PLAN_FEATURES, type Feature } from '../lib/plans';
import { useAppContext } from '../context/useAppContext';

interface PlanGateProps {
    feature: Feature;
    title?: string;
    description?: string;
    children: ReactNode;
    /** Override the icon shown in the lock banner. */
    icon?: ReactNode;
}

const FEATURE_COPY: Record<Feature, { title: string; description: string; highlights: string[] }> = {
    chat: {
        title: 'Unlock Chat with your tenants',
        description: 'Real-time messaging is a paid feature. Send messages, share files and keep everything in one place.',
        highlights: [
            'Real-time messaging with every tenant',
            'Share images, PDFs and documents',
            'Notifications when tenants reply',
        ],
    },
    maintenance: {
        title: 'Unlock Maintenance tickets',
        description: 'Let tenants submit maintenance requests and track them to completion.',
        highlights: [
            'Tenants submit tickets from their portal',
            'Status tracking: new → in progress → resolved',
            'Photo attachments and priority levels',
        ],
    },
    esigning: {
        title: 'Unlock Document e-signing',
        description: 'Send lease agreements and let tenants sign them online — no printing, no scanning.',
        highlights: [
            'Send any document for signature',
            'Legally binding electronic signatures',
            'Automatic copy for both parties',
        ],
    },
    advancedReports: {
        title: 'Unlock Advanced Reports',
        description: 'Get financial insights across your portfolio — occupancy, revenue, overdue, and more.',
        highlights: [
            'Revenue & occupancy dashboards',
            'Overdue rent and late-fee analytics',
            'Export to CSV for your accountant',
        ],
    },
    rentReminders: {
        title: 'Unlock Rent Reminders',
        description: 'Automatically email tenants when rent is due or overdue.',
        highlights: [
            'Automated due-date reminders',
            'Overdue escalation emails',
            'Customizable reminder schedule',
        ],
    },
    teamCollab: {
        title: 'Unlock Team Collaboration',
        description: 'Invite teammates to help manage your portfolio.',
        highlights: [
            'Multiple users per account',
            'Role-based permissions',
            'Audit log of team actions',
        ],
    },
    apiAccess: {
        title: 'Unlock API Access',
        description: 'Integrate RentNova with your existing tools and workflows.',
        highlights: [
            'REST API with full CRUD access',
            'Webhooks for real-time events',
            'API keys per environment',
        ],
    },
    prioritySupport: {
        title: 'Unlock Priority Support',
        description: 'Get faster responses from our support team.',
        highlights: [
            'Priority email support',
            'Response within 24 hours',
            'Dedicated onboarding help',
        ],
    },
    tenantPortal: {
        title: 'Tenant portal',
        description: 'The tenant portal is included on every plan.',
        highlights: [],
    },
    basicRent: {
        title: 'Basic rent tracking',
        description: 'Basic rent tracking is included on every plan.',
        highlights: [],
    },
};

export default function PlanGate({
    feature,
    title,
    description,
    children,
    icon,
}: PlanGateProps) {
    const { user } = useAppContext();

    if (hasFeature(user.plan, feature)) {
        return <>{children}</>;
    }

    const required = minPlanFor(feature);
    const copy = FEATURE_COPY[feature];
    const displayTitle = title ?? copy.title;
    const displayDesc = description ?? copy.description;

    // Build a short list of what the required plan unlocks over Free.
    const requiredFeatures = PLAN_FEATURES[required];
    const freeFeatures = PLAN_FEATURES.Free;
    const unlocked: string[] = [];
    (Object.keys(requiredFeatures) as Feature[]).forEach(f => {
        if (requiredFeatures[f] && !freeFeatures[f] && f !== feature) {
            const label = FEATURE_COPY[f]?.title?.replace(/^Unlock /, '') ?? f;
            unlocked.push(label);
        }
    });

    return (
        <div className="page" style={{ padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
            <div style={{
                maxWidth: 560,
                width: '100%',
                background: '#fff',
                borderRadius: 28,
                overflow: 'hidden',
                boxShadow: '0 30px 60px -12px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.04)',
                border: '1px solid #f1f5f9',
            }}>
                {/* Banner */}
                <div style={{
                    background: 'linear-gradient(135deg, #15803d 0%, #7c3aed 100%)',
                    padding: '2.5rem 2.5rem 3rem',
                    position: 'relative',
                    overflow: 'hidden',
                    color: '#fff',
                }}>
                    <div style={{
                        width: 52,
                        height: 52,
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.25rem',
                    }}>
                        {icon ?? <Lock size={24} color="#fff" />}
                    </div>
                    <div style={{
                        display: 'inline-block',
                        fontSize: '0.6875rem',
                        fontWeight: 800,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.8)',
                        background: 'rgba(255,255,255,0.12)',
                        borderRadius: 99,
                        padding: '4px 10px',
                        marginBottom: 12,
                    }}>
                        {required} plan required
                    </div>
                    <h2 style={{ margin: '0 0 8px', fontSize: '1.75rem', fontWeight: 900, letterSpacing: '-0.03em' }}>
                        {displayTitle}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500, lineHeight: 1.55, maxWidth: 440 }}>
                        {displayDesc}
                    </p>
                </div>

                {/* Highlights */}
                <div style={{ padding: '2rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>
                        What you get
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {copy.highlights.map(h => (
                            <div key={h} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b' }}>
                                <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} />
                                {h}
                            </div>
                        ))}
                        {unlocked.length > 0 && (
                            <div style={{
                                marginTop: 8,
                                fontSize: '0.8125rem',
                                color: '#64748b',
                                fontWeight: 500,
                                lineHeight: 1.55,
                            }}>
                                Also unlocks on {required}: {unlocked.slice(0, 3).join(', ')}
                                {unlocked.length > 3 && ` + ${unlocked.length - 3} more`}.
                            </div>
                        )}
                    </div>
                </div>

                {/* CTA */}
                <div style={{ padding: '1.5rem 2.5rem 2rem', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Link
                        to="/app/landlord/settings"
                        style={{
                            flex: 1,
                            padding: '1rem 1.25rem',
                            borderRadius: 14,
                            border: 'none',
                            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                            fontWeight: 800,
                            fontSize: '0.9375rem',
                            color: '#fff',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            boxShadow: '0 12px 24px -8px rgba(22,163,74,0.45)',
                        }}
                    >
                        <Zap size={16} /> Upgrade to {required}
                        <ArrowRight size={16} />
                    </Link>
                </div>

                {/* Current plan footnote */}
                <div style={{
                    background: '#f8fafc',
                    padding: '1rem 2.5rem',
                    fontSize: '0.8125rem',
                    color: '#64748b',
                    fontWeight: 500,
                    borderTop: '1px solid #f1f5f9',
                    textAlign: 'center',
                }}>
                    You are currently on the <strong style={{ color: '#1a1d2e' }}>{user.plan}</strong> plan
                    {user.subscription_status && user.subscription_status !== 'active' && user.subscription_status !== 'trialing' && (
                        <> · status: <strong style={{ color: '#dc2626' }}>{user.subscription_status}</strong></>
                    )}
                </div>
            </div>
        </div>
    );
}
